/* ============================================
   FLIPKART-INSPIRED E-COMMERCE - GLOBAL JS
   ============================================ */

// ============================================
// DATA MANAGEMENT - LocalStorage
// ============================================

// Initialize data structures
const initializeData = () => {
  if (!localStorage.getItem('cart')) {
    localStorage.setItem('cart', JSON.stringify([]));
  }
  if (!localStorage.getItem('wishlist')) {
    localStorage.setItem('wishlist', JSON.stringify([]));
  }
  if (!localStorage.getItem('recentlyViewed')) {
    localStorage.setItem('recentlyViewed', JSON.stringify([]));
  }
  if (!localStorage.getItem('user')) {
    localStorage.setItem('user', JSON.stringify({
      name: 'Guest User',
      email: '',
      phone: '',
      isLoggedIn: false
    }));
  }
};

// Cart functions
const getCart = () => JSON.parse(localStorage.getItem('cart') || '[]');

const addToCart = (product, quantity = 1) => {
  const cart = getCart();
  const existingItem = cart.find(item => item.id === product.id);

  if (existingItem) {
    existingItem.quantity += quantity;
  } else {
    cart.push({ ...product, quantity });
  }

  localStorage.setItem('cart', JSON.stringify(cart));
  updateCartCount();
  showToast('Success', 'Product added to cart', 'success');
  return cart;
};

const removeFromCart = (productId) => {
  let cart = getCart();
  cart = cart.filter(item => item.id !== productId);
  localStorage.setItem('cart', JSON.stringify(cart));
  updateCartCount();
  showToast('Removed', 'Product removed from cart', 'info');
  return cart;
};

const updateCartQuantity = (productId, quantity) => {
  const cart = getCart();
  const item = cart.find(item => item.id === productId);

  if (item) {
    if (quantity <= 0) {
      return removeFromCart(productId);
    }
    item.quantity = quantity;
    localStorage.setItem('cart', JSON.stringify(cart));
    updateCartCount();
  }
  return cart;
};

const clearCart = () => {
  localStorage.setItem('cart', JSON.stringify([]));
  updateCartCount();
};

const getCartTotal = () => {
  const cart = getCart();
  return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
};

const getCartCount = () => {
  const cart = getCart();
  return cart.reduce((count, item) => count + item.quantity, 0);
};

// Wishlist functions
const getWishlist = () => JSON.parse(localStorage.getItem('wishlist') || '[]');

const addToWishlist = (product) => {
  const wishlist = getWishlist();
  const exists = wishlist.find(item => item.id === product.id);

  if (!exists) {
    wishlist.push(product);
    localStorage.setItem('wishlist', JSON.stringify(wishlist));
    updateWishlistCount();
    showToast('Added', 'Product added to wishlist', 'success');
  } else {
    showToast('Already in wishlist', 'This product is already in your wishlist', 'info');
  }
  return wishlist;
};

const removeFromWishlist = (productId) => {
  let wishlist = getWishlist();
  wishlist = wishlist.filter(item => item.id !== productId);
  localStorage.setItem('wishlist', JSON.stringify(wishlist));
  updateWishlistCount();
  showToast('Removed', 'Product removed from wishlist', 'info');
  return wishlist;
};

const isInWishlist = (productId) => {
  const wishlist = getWishlist();
  return wishlist.some(item => item.id === productId);
};

const getWishlistCount = () => {
  const wishlist = getWishlist();
  return wishlist.length;
};

// Recently Viewed functions
const addToRecentlyViewed = (product) => {
  let recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');

  // Remove if already exists
  recentlyViewed = recentlyViewed.filter(item => item.id !== product.id);

  // Add to beginning
  recentlyViewed.unshift(product);

  // Keep only last 20 items
  if (recentlyViewed.length > 20) {
    recentlyViewed = recentlyViewed.slice(0, 20);
  }

  localStorage.setItem('recentlyViewed', JSON.stringify(recentlyViewed));
};

const getRecentlyViewed = () => JSON.parse(localStorage.getItem('recentlyViewed') || '[]');

// ============================================
// UI UPDATE FUNCTIONS
// ============================================

const updateCartCount = () => {
  const count = getCartCount();
  const badges = document.querySelectorAll('.cart-count, .nav-icon-badge[data-type="cart"]');
  badges.forEach(badge => {
    badge.textContent = count;
    badge.style.display = count > 0 ? 'block' : 'none';
  });
};

const updateWishlistCount = () => {
  const count = getWishlistCount();
  const badges = document.querySelectorAll('.wishlist-count, .nav-icon-badge[data-type="wishlist"]');
  badges.forEach(badge => {
    badge.textContent = count;
    badge.style.display = count > 0 ? 'block' : 'none';
  });
};

// ============================================
// TOAST NOTIFICATIONS
// ============================================

let toastContainer = null;

const createToastContainer = () => {
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.className = 'toast-container';
    document.body.appendChild(toastContainer);
  }
  return toastContainer;
};

const showToast = (title, message, type = 'info', duration = 3000) => {
  const container = createToastContainer();

  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;

  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ'
  };

  toast.innerHTML = `
    <div class="toast-icon">${icons[type] || icons.info}</div>
    <div class="toast-content">
      <div class="toast-title">${title}</div>
      <div class="toast-message">${message}</div>
    </div>
  `;

  container.appendChild(toast);

  // Auto remove after duration
  setTimeout(() => {
    toast.style.animation = 'fadeOut 300ms ease-out';
    setTimeout(() => {
      container.removeChild(toast);
    }, 300);
  }, duration);
};

// ============================================
// MODAL FUNCTIONS
// ============================================

const openModal = (modalId) => {
  const modal = document.getElementById(modalId);
  if (modal) {
    modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }
};

const closeModal = (modalId) => {
  const modal = document.getElementById(modalId);
  if (modal) {
    modal.classList.remove('active');
    document.body.style.overflow = '';
  }
};

// Close modal on backdrop click
document.addEventListener('click', (e) => {
  if (e.target.classList.contains('modal-backdrop')) {
    closeModal(e.target.id);
  }
});

// ============================================
// SEARCH FUNCTIONALITY
// ============================================

let searchTimeout = null;

const initializeSearch = () => {
  const searchInput = document.querySelector('.search-input');
  const searchBtn = document.querySelector('.search-btn');

  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        handleSearch(e.target.value);
      }, 300);
    });

    searchInput.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        performSearch(e.target.value);
      }
    });
  }

  if (searchBtn) {
    searchBtn.addEventListener('click', () => {
      const query = searchInput.value;
      performSearch(query);
    });
  }
};

const handleSearch = (query) => {
  // This will show suggestions (can be implemented later)
  console.log('Search query:', query);
};

const performSearch = (query) => {
  if (query.trim()) {
    // Redirect to search results page with query
    window.location.href = `pages/search-results.html?q=${encodeURIComponent(query)}`;
  }
};

// ============================================
// MOBILE MENU TOGGLE
// ============================================

const initializeMobileMenu = () => {
  const menuToggle = document.querySelector('.mobile-menu-toggle');
  const mobileMenu = document.querySelector('.mobile-menu');

  if (menuToggle && mobileMenu) {
    menuToggle.addEventListener('click', () => {
      mobileMenu.classList.toggle('active');
      document.body.style.overflow = mobileMenu.classList.contains('active') ? 'hidden' : '';
    });
  }
};

// ============================================
// IMAGE LAZY LOADING
// ============================================

const initializeLazyLoading = () => {
  const images = document.querySelectorAll('img[data-src]');

  const imageObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
        observer.unobserve(img);
      }
    });
  });

  images.forEach(img => imageObserver.observe(img));
};

// ============================================
// CAROUSEL FUNCTIONALITY
// ============================================

class Carousel {
  constructor(element, options = {}) {
    this.carousel = element;
    this.slides = element.querySelectorAll('.carousel-slide');
    this.currentSlide = 0;
    this.autoplay = options.autoplay !== false;
    this.interval = options.interval || 3000;
    this.autoplayTimer = null;

    this.init();
  }

  init() {
    this.createControls();
    this.createIndicators();
    this.showSlide(0);

    if (this.autoplay) {
      this.startAutoplay();
    }

    // Pause on hover
    this.carousel.addEventListener('mouseenter', () => this.stopAutoplay());
    this.carousel.addEventListener('mouseleave', () => {
      if (this.autoplay) this.startAutoplay();
    });
  }

  createControls() {
    const prevBtn = document.createElement('button');
    prevBtn.className = 'carousel-control carousel-prev';
    prevBtn.innerHTML = '‹';
    prevBtn.addEventListener('click', () => this.prevSlide());

    const nextBtn = document.createElement('button');
    nextBtn.className = 'carousel-control carousel-next';
    nextBtn.innerHTML = '›';
    nextBtn.addEventListener('click', () => this.nextSlide());

    this.carousel.appendChild(prevBtn);
    this.carousel.appendChild(nextBtn);
  }

  createIndicators() {
    const indicators = document.createElement('div');
    indicators.className = 'carousel-indicators';

    this.slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.className = 'carousel-indicator';
      dot.addEventListener('click', () => this.showSlide(index));
      indicators.appendChild(dot);
    });

    this.carousel.appendChild(indicators);
  }

  showSlide(index) {
    this.currentSlide = (index + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, i) => {
      slide.style.display = i === this.currentSlide ? 'block' : 'none';
    });

    const indicators = this.carousel.querySelectorAll('.carousel-indicator');
    indicators.forEach((indicator, i) => {
      indicator.classList.toggle('active', i === this.currentSlide);
    });
  }

  nextSlide() {
    this.showSlide(this.currentSlide + 1);
  }

  prevSlide() {
    this.showSlide(this.currentSlide - 1);
  }

  startAutoplay() {
    this.autoplayTimer = setInterval(() => this.nextSlide(), this.interval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
}

// Initialize all carousels
const initializeCarousels = () => {
  document.querySelectorAll('.carousel').forEach(carousel => {
    new Carousel(carousel);
  });
};

// ============================================
// HORIZONTAL SCROLL FUNCTIONALITY
// ============================================

const initializeHorizontalScroll = () => {
  const scrollContainers = document.querySelectorAll('.horizontal-scroll');

  scrollContainers.forEach(container => {
    let isDown = false;
    let startX;
    let scrollLeft;

    container.addEventListener('mousedown', (e) => {
      isDown = true;
      container.style.cursor = 'grabbing';
      startX = e.pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
    });

    container.addEventListener('mouseleave', () => {
      isDown = false;
      container.style.cursor = 'grab';
    });

    container.addEventListener('mouseup', () => {
      isDown = false;
      container.style.cursor = 'grab';
    });

    container.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 2;
      container.scrollLeft = scrollLeft - walk;
    });
  });
};

// ============================================
// PRODUCT CARD QUICK ACTIONS
// ============================================

const setupProductCardActions = () => {
  // Add to cart buttons
  document.addEventListener('click', (e) => {
    if (e.target.closest('.quick-add-to-cart')) {
      const btn = e.target.closest('.quick-add-to-cart');
      const productData = JSON.parse(btn.dataset.product || '{}');
      addToCart(productData);
    }

    // Wishlist toggle
    if (e.target.closest('.wishlist-toggle')) {
      const btn = e.target.closest('.wishlist-toggle');
      const productData = JSON.parse(btn.dataset.product || '{}');

      if (isInWishlist(productData.id)) {
        removeFromWishlist(productData.id);
        btn.classList.remove('active');
      } else {
        addToWishlist(productData);
        btn.classList.add('active');
      }
    }
  });
};

// ============================================
// STICKY HEADER
// ============================================

const initializeStickyHeader = () => {
  const header = document.querySelector('.header');
  if (!header) return;

  let lastScroll = 0;

  window.addEventListener('scroll', () => {
    const currentScroll = window.pageYOffset;

    if (currentScroll > 100) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }

    lastScroll = currentScroll;
  });
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatPrice = (price) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(price);
};

const calculateDiscount = (originalPrice, salePrice) => {
  return Math.round(((originalPrice - salePrice) / originalPrice) * 100);
};

const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// ============================================
// SAMPLE PRODUCT DATA
// ============================================

const sampleProducts = [
  {
    id: 1,
    name: 'Wireless Bluetooth Headphones with Noise Cancellation',
    price: 1999,
    originalPrice: 3999,
    rating: 4.5,
    reviews: 1234,
    image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=500&q=80',
    category: 'Electronics',
    brand: 'TechBrand',
    inStock: true
  },
  {
    id: 2,
    name: 'Smart Fitness Watch with Heart Rate Monitor',
    price: 2499,
    originalPrice: 4999,
    rating: 4.3,
    reviews: 856,
    image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=500&q=80',
    category: 'Electronics',
    brand: 'FitTech',
    inStock: true
  },
  {
    id: 3,
    name: 'Premium Cotton Casual Shirt - Slim Fit',
    price: 599,
    originalPrice: 1299,
    rating: 4.1,
    reviews: 432,
    image: 'https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=500&q=80',
    category: 'Fashion',
    brand: 'StyleCo',
    inStock: true
  },
  {
    id: 4,
    name: 'Running Shoes - Lightweight & Comfortable',
    price: 1799,
    originalPrice: 2999,
    rating: 4.6,
    reviews: 2341,
    image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=500&q=80',
    category: 'Fashion',
    brand: 'SportMax',
    inStock: true
  },
  {
    id: 5,
    name: 'Laptop Backpack with USB Charging Port',
    price: 899,
    originalPrice: 1799,
    rating: 4.4,
    reviews: 567,
    image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=500&q=80',
    category: 'Accessories',
    brand: 'TravelPro',
    inStock: true
  },
  {
    id: 6,
    name: 'Wireless Optical Mouse - Ergonomic Design',
    price: 399,
    originalPrice: 799,
    rating: 4.2,
    reviews: 891,
    image: 'https://images.unsplash.com/photo-1527864550417-7fd91fc51a46?w=500&q=80',
    category: 'Electronics',
    brand: 'TechBrand',
    inStock: true
  },
  {
    id: 7,
    name: 'Stainless Steel Insulated Water Bottle 1L',
    price: 349,
    originalPrice: 699,
    rating: 4.5,
    reviews: 1123,
    image: 'https://images.unsplash.com/photo-1602143407151-0111419516eb?w=500&q=80',
    category: 'Home',
    brand: 'EcoLife',
    inStock: true
  },
  {
    id: 8,
    name: 'Premium Yoga Mat - Non-Slip Surface',
    price: 499,
    originalPrice: 999,
    rating: 4.3,
    reviews: 678,
    image: 'https://images.unsplash.com/photo-1601925260368-ae2f83cf8b7f?w=500&q=80',
    category: 'Sports',
    brand: 'FitLife',
    inStock: true
  },
  {
    id: 9,
    name: 'Portable Bluetooth Speaker - Waterproof',
    price: 1299,
    originalPrice: 2499,
    rating: 4.4,
    reviews: 945,
    image: 'https://images.unsplash.com/photo-1608043152269-423dbba4e7e1?w=500&q=80',
    category: 'Electronics',
    brand: 'SoundMax',
    inStock: true
  },
  {
    id: 10,
    name: 'Designer Sunglasses - UV Protection',
    price: 799,
    originalPrice: 1599,
    rating: 4.2,
    reviews: 523,
    image: 'https://images.unsplash.com/photo-1511499767150-a48a237f0083?w=500&q=80',
    category: 'Fashion',
    brand: 'StyleCo',
    inStock: true
  },
  {
    id: 11,
    name: 'Mechanical Gaming Keyboard - RGB Backlit',
    price: 2999,
    originalPrice: 4999,
    rating: 4.7,
    reviews: 1567,
    image: 'https://images.unsplash.com/photo-1587829741301-dc798b91a603?w=500&q=80',
    category: 'Electronics',
    brand: 'GameTech',
    inStock: true
  },
  {
    id: 12,
    name: 'Ceramic Coffee Mug Set - 6 Pieces',
    price: 449,
    originalPrice: 899,
    rating: 4.3,
    reviews: 234,
    image: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?w=500&q=80',
    category: 'Home',
    brand: 'HomeEssentials',
    inStock: true
  },
  {
    id: 13,
    name: 'Denim Jeans - Stretchable Fabric',
    price: 899,
    originalPrice: 1799,
    rating: 4.1,
    reviews: 678,
    image: 'https://images.unsplash.com/photo-1542272617-08f086302542?w=500&q=80',
    category: 'Fashion',
    brand: 'DenimCo',
    inStock: true
  },
  {
    id: 14,
    name: 'LED Desk Lamp - Adjustable Brightness',
    price: 599,
    originalPrice: 1199,
    rating: 4.4,
    reviews: 456,
    image: 'https://images.unsplash.com/photo-1534073828943-f801091a7d58?w=500&q=80',
    category: 'Home',
    brand: 'LightUp',
    inStock: true
  },
  {
    id: 15,
    name: 'Protein Shaker Bottle - 700ml',
    price: 249,
    originalPrice: 499,
    rating: 4.2,
    reviews: 789,
    image: 'https://images.unsplash.com/photo-1570183562479-7f88e4d209c7?w=500&q=80',
    category: 'Sports',
    brand: 'FitLife',
    inStock: true
  },
  {
    id: 16,
    name: 'Smartphone Stand - Adjustable Angle',
    price: 299,
    originalPrice: 599,
    rating: 4.0,
    reviews: 345,
    image: 'https://images.unsplash.com/photo-1586105251261-72a756497a11?w=500&q=80',
    category: 'Accessories',
    brand: 'TechBrand',
    inStock: true
  }
];

// ============================================
// INITIALIZATION
// ============================================

document.addEventListener('DOMContentLoaded', () => {
  // Initialize data
  initializeData();

  // Update UI counts
  updateCartCount();
  updateWishlistCount();

  // Initialize features
  initializeSearch();
  initializeMobileMenu();
  initializeLazyLoading();
  initializeCarousels();
  initializeHorizontalScroll();
  initializeStickyHeader();
  setupProductCardActions();

  console.log('E-commerce app initialized');
});

// Export functions for use in other scripts
window.ecommerce = {
  // Cart
  getCart,
  addToCart,
  removeFromCart,
  updateCartQuantity,
  clearCart,
  getCartTotal,
  getCartCount,

  // Wishlist
  getWishlist,
  addToWishlist,
  removeFromWishlist,
  isInWishlist,
  getWishlistCount,

  // Recently Viewed
  addToRecentlyViewed,
  getRecentlyViewed,

  // UI
  showToast,
  openModal,
  closeModal,

  // Utilities
  formatPrice,
  calculateDiscount,

  // Data
  sampleProducts
};
